///WEBSITE CODE

import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  X,
  User,
  Briefcase,
  FileText,
  ArrowRight,
  Trash2,
  Pencil,
  Upload,
  ZoomIn,
  ZoomOut,
  Camera,
  Smartphone,
  Mail,
  Eye,
  EyeOff,
  Shield,
  MapPin,
  RotateCcw,
  AlertTriangle,
  Info,
  Lock,
  IdCard,
} from "lucide-react";
import logo from "../assets/logo.png";
import welcome from "../assets/welcomepage.png";
import { supabase } from "../supabaseClient";

// ─── DESIGN TOKENS (matches LandingPage.jsx palette) ─────────────────────────
const COLOR = {
  cream: "#F6F7F1",
  white: "#FFFFFF",
  ink: "#376621",
  muted: "#5C6B60",
  green900: "#559f33",
  green700: "#75d83f",
  green600: "#2E8B4F",
  green500: "#3FA85E",
  lime: "#D8F24C",
  limeInk: "#376621",
  line: "#E1E6D8",
  tintGreen: "rgba(29, 188, 58, 0.07)",
  tintLime: "rgba(216,242,76,0.16)",
  shadow: "rgba(14,59,34,0.12)",
  shadowStrong: "rgba(14,59,34,0.24)",
};

// ─── AUDIT TRAIL ─────────────────────────────────────────────────────────────
const auditLog = (() => {
  const logs = [];
  return {
    record: (action, data = {}) => {
      const entry = {
        id: `AUDIT-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        action,
        data,
        sessionId:
          sessionStorage.getItem("session_id") ||
          (() => {
            const id = `SES-${Date.now()}`;
           // sessionStorage.setItem("session_id", id);
            return id;
          })(),
      };
      logs.push(entry);
      console.info("[AUDIT TRAIL]", entry);
      return entry;
    },
    getAll: () => [...logs],
  };
})();

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CONCEPTS = ["Coffee Spot", "iPharma Mart", "iFuel"];
const CIVIL = ["Single", "Married", "Widowed", "Separated"];
const SUFFIXES = [
  "",
  "Jr.",
  "Sr.",
  "II",
  "III",
  "IV",
  "V",
  "MD",
  "PhD",
  "Esq.",
];
const PAYMENT_MODES = ["Cash", "Bank Transfer", "Cheque"];
const EMPLOYMENT_TYPES = [
  "Full-time Employee",
  "Part-time Employee",
  "Self-Employed",
  "Business Owner",
  "Freelancer",
  "Retired",
  "Unemployed",
];
const NATIONALITIES = ["Filipino", "Others"];

const VALID_ID_TYPES = [
  "Philippine Passport",
  "SSS ID",
  "GSIS ID",
  "PhilHealth ID",
  "Pag-IBIG ID",
  "Driver's License",
  "PRC ID",
  "Voter's ID",
  "National ID (PhilSys)",
  "Senior Citizen ID",
  "PWD ID",
  "UMID",
];

const MAX_LOI_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

const uploadLoi = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-loi`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  const { url } = await res.json();
  return url;
};

const capitalize = (v) => v.replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// ─── MODAL ────────────────────────────────────────────────────────────────────
function AlertModal({
  open,
  type,
  message,
  onClose,
  onConfirm,
  autoCloseMs = 2200,
}) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (onConfirm) onConfirm();
      else onClose();
    }, autoCloseMs);
    return () => clearTimeout(t);
  }, [open, onConfirm, onClose, autoCloseMs]);

  if (!open) return null;
  const isSuccess = type === "success";
  return (
    <div style={S.overlay}>
      <div style={S.modalBox}>
        <div
          style={{
            ...S.iconWrap,
            background: isSuccess ? COLOR.tintGreen : "#fdecea",
          }}
        >
          {isSuccess ? (
            <CheckCircle2 size={38} color={COLOR.green900} strokeWidth={2} />
          ) : (
            <AlertCircle size={38} color="#c62828" strokeWidth={2} />
          )}
        </div>
        <p style={S.modalMsg}>{message}</p>
        <div style={S.btnRow}>
          {onConfirm ? (
            <>
              <button style={{ ...S.btn, ...S.btnOutline }} onClick={onClose}>
                Cancel
              </button>
              <button style={{ ...S.btn, ...S.btnSolid }} onClick={onConfirm}>
                Confirm
              </button>
            </>
          ) : (
            <button style={{ ...S.btn, ...S.btnSolid }} onClick={onClose}>
              OK
            </button>
          )}
        </div>
        <button style={S.closeBtn} onClick={onClose}>
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

// ─── TERMS MODAL ─────────────────────────────────────────────────────────────

function TermsModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div style={S.overlay}>
      <div
        style={{
          ...S.modalBox,
          width: 700,
          maxWidth: "95vw",
          textAlign: "left",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              color: COLOR.green900,
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            Terms & Conditions
          </h3>

          <button style={S.closeBtn} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div
          style={{
            overflowY: "auto",
            flex: 1,
            paddingRight: 8,
            fontSize: 13,
            color: COLOR.ink,
            lineHeight: 1.9,
          }}
        >
          <p>
            By completing and submitting this Franchise Application Form, the
            applicant agrees to the following Terms and Conditions set by{" "}
            <strong>iFranchise Business and Services Corporation</strong>.
          </p>

          <p>
            <strong>1. DATA PRIVACY AND CONSENT (RA 10173)</strong>
            <br />
            By submitting this application, the applicant voluntarily gives
            consent to{" "}
            <strong>iFranchise Business and Services Corporation</strong> to
            <strong>
              {" "}
              collect, process, store, and manage personal information
            </strong>
            in accordance with the
            <strong> Data Privacy Act of 2012 (Republic Act No. 10173)</strong>.
            <br />
            <br />
            Information collected may be used for
            <strong>
              {" "}
              franchise application processing, identity verification,
              communication, internal record keeping, fraud prevention,
              verification, and legal compliance
            </strong>
            .
            <br />
            <br />
            The company agrees to implement
            <strong>
              {" "}
              reasonable security measures to protect submitted personal
              information
            </strong>
            against unauthorized access, disclosure, misuse, or loss.
          </p>

          <p>
            <strong>2. APPLICANT INFORMATION AND DECLARATION</strong>
            <br />
            The applicant confirms that all personal information submitted in
            the application form is
            <strong> complete, true, accurate, and updated</strong>, including
            but not limited to
            <strong>
              {" "}
              full name, date of birth, civil status, gender, nationality,
              contact details, address, employment information, income details,
              uploaded IDs, and Letter of Intent
            </strong>
            .
            <br />
            <br />
            The applicant understands that any
            <strong>
              {" "}
              false, misleading, incomplete, or fraudulent information
            </strong>
            may result in
            <strong>
              {" "}
              rejection, suspension, permanent disqualification, or legal action
            </strong>
            .
          </p>

          <p>
            <strong>3. ID VERIFICATION AND DOCUMENT AUTHENTICITY</strong>
            <br />
            The applicant agrees to upload only
            <strong>
              {" "}
              valid and authentic government-issued identification documents
            </strong>
            for verification purposes.
            <br />
            <br />
            The applicant authorizes the company to
            <strong> verify the authenticity of submitted IDs</strong> and
            acknowledges that
            <strong> falsified or tampered IDs are strictly prohibited</strong>.
          </p>

          <p>
            <strong>4. OCR AND AUTO-FILLED INFORMATION CONSENT</strong>
            <br />
            The applicant acknowledges that the system may use
            <strong> OCR (Optical Character Recognition) technology</strong>
            to scan uploaded IDs and automatically populate application fields.
            <br />
            <br />
            The applicant is responsible for
            <strong>
              {" "}
              reviewing and correcting all auto-filled information
            </strong>
            before submission.
          </p>

          <p>
            <strong>5. FRANCHISE APPLICATION EVALUATION</strong>
            <br />
            Submission of this application form does
            <strong> not guarantee franchise approval</strong>, ownership, or
            partnership rights.
            <br />
            <br />
            All applications are subject to
            <strong>
              {" "}
              document verification, financial assessment, background checking,
              business evaluation, and internal approval procedures
            </strong>
            .
            <br />
            <br />
            The company reserves the right to
            <strong>
              {" "}
              approve, reject, suspend, or terminate any application
            </strong>
            at its sole discretion.
          </p>

          <p>
            <strong>6. PAYMENT TERMS</strong>
            <br />
            Any
            <strong>
              {" "}
              fees, reservation payments, processing fees, or franchise-related
              payments
            </strong>
            made through the selected payment mode are subject to company
            policies.
          </p>

          <p>
            <strong>7. EMPLOYMENT AND FINANCIAL INFORMATION</strong>
            <br />
            The applicant certifies that all
            <strong>
              {" "}
              employment, income, and business information submitted
            </strong>
            are accurate and may be used for
            <strong> financial and application evaluation purposes</strong>.
          </p>

          <p>
            <strong>8. LETTER OF INTENT SUBMISSION</strong>
            <br />
            The applicant agrees to upload a
            <strong> valid PDF copy of the required Letter of Intent</strong>.
            Uploaded documents must be
            <strong>
              {" "}
              readable, authentic, and free from malicious content
            </strong>
            .
          </p>

          <p>
            <strong>9. COMMUNICATION CONSENT</strong>
            <br />
            The applicant authorizes the company to contact them through
            <strong>
              {" "}
              mobile number, alternate mobile number, email address, or other
              communication channels
            </strong>
            regarding
            <strong>
              {" "}
              application updates, interviews, payment confirmations, and
              franchise-related notifications
            </strong>
            .
          </p>

          <p>
            <strong>10. AUDIT TRAIL AND SECURITY LOGS</strong>
            <br />
            All actions performed during the franchise application process may
            be recorded, including
            <strong>
              {" "}
              timestamps, session identifiers, uploaded files, and system
              activities
            </strong>
            for security and monitoring purposes.
          </p>

          <p>
            <strong>11. OTP VERIFICATION</strong>
            <br />
            The applicant may receive a<strong> One-Time Password (OTP)</strong>
            through the registered email address for
            <strong> identity verification and security purposes</strong>.
          </p>

          <p>
            <strong>12. INTELLECTUAL PROPERTY AND SYSTEM USAGE</strong>
            <br />
            The applicant agrees not to
            <strong>
              {" "}
              attempt unauthorized access, upload malicious files, manipulate
              records, or misuse the system
            </strong>
            .
          </p>

          <p>
            <strong>13. LIMITATION OF LIABILITY</strong>
            <br />
            The company shall not be held liable for
            <strong>
              {" "}
              delays caused by incomplete submissions, OCR inaccuracies,
              technical issues, or unauthorized access caused by applicant
              negligence
            </strong>
            .
          </p>

          <p>
            <strong>14. GOVERNING LAW</strong>
            <br />
            These Terms and Conditions shall be governed by the
            <strong> laws of the Republic of the Philippines</strong>.
          </p>

          <p>
            <strong>15. AMENDMENTS AND MODIFICATIONS</strong>
            <br />
            The company reserves the right to
            <strong>
              {" "}
              modify or update these Terms and Conditions at any time
            </strong>
            without prior notice.
          </p>

          <p>
            <strong>16. APPLICANT AGREEMENT</strong>
            <br />
            By checking the agreement checkbox and submitting the application,
            the applicant confirms that:
          </p>

          <ul style={{ paddingLeft: 20 }}>
            <li>
              They have <strong>read and understood</strong> these Terms and
              Conditions
            </li>
            <li>
              All submitted information is
              <strong> accurate and legitimate</strong>
            </li>
            <li>
              They voluntarily <strong>agree to all stated provisions</strong>
            </li>
            <li>
              They consent to
              <strong>
                {" "}
                verification, evaluation, and data processing procedures
              </strong>
            </li>
          </ul>

          <p style={{ color: COLOR.muted, fontSize: 12, marginTop: 24 }}>
            Last Updated: May 12, 2026
          </p>
        </div>

        <button
          style={{
            ...S.btn,
            ...S.btnSolid,
            marginTop: 16,
            width: "100%",
          }}
          onClick={onClose}
        >
          I Have Read the Terms
        </button>
      </div>
    </div>
  );
}

// ─── OTP MODAL ───────────────────────────────────────────────────────────────
function OtpModal({
  open,
  email,
  onVerify,
  onClose,
  maxAttempts = 3,
  expectedOtp,
  onResend,
}) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    if (!open) return;
    setOtp(["", "", "", "", "", ""]);
    setAttempts(0);
    setError("");
    setCountdown(60);
    setCanResend(false);
    auditLog.record("OTP_SENT", {
      email,
      channel: "email",
      timestamp: new Date().toISOString(),
    });
  }, [open]);

  useEffect(() => {
    if (!open || canResend) return;
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(t);
          setCanResend(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open, canResend]);

  const handleInput = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const verify = () => {
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }
    auditLog.record("OTP_ATTEMPT", {
      attempt: attempts + 1,
      maskedOtp: "XXXXXX",
    });
    if (code === expectedOtp) {
      auditLog.record("OTP_VERIFIED", { success: true });
      onVerify(true);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      auditLog.record("OTP_FAILED", {
        attempt: newAttempts,
        remaining: maxAttempts - newAttempts,
      });
      if (newAttempts >= maxAttempts) {
        auditLog.record("OTP_MAX_ATTEMPTS_REACHED");
        setError(
          `Maximum ${maxAttempts} attempts reached. Please request a new OTP.`,
        );
        setOtp(["", "", "", "", "", ""]);
        setCanResend(true);
      } else {
        setError(
          `Incorrect OTP. ${maxAttempts - newAttempts} attempt(s) remaining.`,
        );
        setOtp(["", "", "", "", "", ""]);
        refs.current[0]?.focus();
      }
    }
  };

  const resend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setAttempts(0);
    setError("");
    setSending(true);

    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/send-application-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp: newOtp }),
        },
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to resend verification code.");
      }

      onResend(newOtp);
      setCountdown(60);
      setCanResend(false);
      auditLog.record("OTP_RESENT", {
        email,
        channel: "email",
      });
    } catch (err) {
      console.error("OTP resend error:", err);
      setError(
        err.message ||
          "Could not resend the verification code. Please try again.",
      );
      setCanResend(true);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <div style={S.overlay}>
      <div style={{ ...S.modalBox, width: 380 }}>
        <div style={{ ...S.iconWrap, background: COLOR.tintGreen }}>
          <Mail size={38} color={COLOR.green900} strokeWidth={2} />
        </div>
        <h3
          style={{
            margin: "0 0 6px",
            color: COLOR.ink,
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          Email OTP Verification
        </h3>
        <p
          style={{
            margin: "0 0 20px",
            color: COLOR.muted,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          A 6-digit verification code was sent to <strong>{email}</strong>.
          <br />
        </p>
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          {otp.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleInput(i, e.target.value)}
              onKeyDown={(e) => handleKey(i, e)}
              style={{
                width: 44,
                height: 52,
                textAlign: "center",
                fontSize: 22,
                fontWeight: 700,
                borderRadius: 10,
                border: error ? "2px solid #d32f2f" : `2px solid ${COLOR.line}`,
                outline: "none",
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                background: COLOR.white,
                color: COLOR.ink,
              }}
            />
          ))}
        </div>
        {error && (
          <p
            style={{
              color: "#d32f2f",
              fontSize: 12,
              margin: "0 0 12px",
              fontWeight: 600,
            }}
          >
            {error}
          </p>
        )}
        <button
          style={{ ...S.btn, ...S.btnSolid, width: "100%", marginBottom: 10 }}
          onClick={verify}
        >
          Verify OTP
        </button>
        <p style={{ margin: 0, fontSize: 12, color: COLOR.muted }}>
          {canResend ? (
            <button
              onClick={resend}
              disabled={sending}
              style={{
                background: "none",
                border: "none",
                color: COLOR.green900,
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 12,
                padding: 0,
              }}
            >
              {sending ? "Resending…" : "Resend OTP"}
            </button>
          ) : (
            <>
              Resend in{" "}
              <strong style={{ color: COLOR.green900 }}>{countdown}s</strong>
            </>
          )}
        </p>
        <button style={S.closeBtn} onClick={onClose}>
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

function IdScannerModal({ open, onComplete, onClose }) {
  const [step, setStep] = useState("type");
  const [idType, setIdType] = useState("");
  const [frontImg, setFrontImg] = useState(null);
  const [backImg, setBackImg] = useState(null);
  const [faceImg, setFaceImg] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [idValid, setIdValid] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedOcr, setEditedOcr] = useState({});
  const [frontZoom, setFrontZoom] = useState(1);
  const [backZoom, setBackZoom] = useState(1);
  const [frontRotation, setFrontRotation] = useState(0);
  const [backRotation, setBackRotation] = useState(0);

  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [countdown, setCountdown] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const countdownRef = useRef(null);

  const frontRef = useRef();
  const backRef = useRef();
  const faceFileRef = useRef();

  // ── Reset on close ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      stopCamera();
      setStep("type");
      setIdType("");
      setFrontImg(null);
      setBackImg(null);
      setFaceImg(null);
      setFrontZoom(1);
      setBackZoom(1);
      setFrontRotation(0);
      setBackRotation(0);
      setOcrResult(null);
      setIdValid(null);
      setIsEditing(false);
      setEditedOcr({});
      setCameraError("");
      setCountdown(null);
    }
  }, [open]);

  const startCamera = async () => {
    setCameraError("");
    setFaceImg(null);

    try {
      // Basic browser support check
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          "Camera access is not supported in this browser or the page is not using HTTPS.",
        );
      }

      // Stop any previous stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current.play();
            setCameraActive(true);
            setCameraError("");
          } catch (playError) {
            console.error("Video play error:", playError);
            setCameraError(
              "Camera opened, but the video preview could not start. Please try again.",
            );
          }
        };
      } else {
        setCameraActive(true);
      }
    } catch (err) {
      console.error("CAMERA ERROR:", {
        name: err?.name,
        message: err?.message,
        error: err,
      });

      setCameraActive(false);

      switch (err?.name) {
        case "NotAllowedError":
        case "PermissionDeniedError":
          setCameraError(
            "Camera access was blocked by the browser or your device settings. Please allow camera access for this site and check your system camera privacy settings.",
          );
          break;

        case "NotFoundError":
        case "DevicesNotFoundError":
          setCameraError("No camera was detected on this device.");
          break;

        case "NotReadableError":
        case "TrackStartError":
          setCameraError(
            "Your camera is currently unavailable. Close other apps using the camera, then try again.",
          );
          break;

        case "OverconstrainedError":
        case "ConstraintNotSatisfiedError":
          setCameraError(
            "The selected camera does not support the requested settings. Please try another camera.",
          );
          break;

        case "SecurityError":
          setCameraError(
            "Camera access is blocked for security reasons. Make sure you are using HTTPS.",
          );
          break;

        default:
          setCameraError(
            err?.message ||
              "Could not access the camera. Please check your browser and device camera settings.",
          );
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCameraActive(false);
    setCountdown(null);
  };

  const startCountdown = () => {
    let count = 3;
    setCountdown(count);
    countdownRef.current = setInterval(() => {
      count -= 1;
      if (count === 0) {
        clearInterval(countdownRef.current);
        setCountdown(null);
        capturePhoto();
      } else {
        setCountdown(count);
      }
    }, 1000);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setFaceImg(dataUrl);
    stopCamera();
    auditLog.record("FACE_CAPTURED");
  };

  const handleFile = (side, file) => {
    if (!file) return;

    const isJpeg =
      file.type === "image/jpeg" || /\.(jpg|jpeg)$/i.test(file.name);

    if (!isJpeg) {
      setCameraError("Only JPG/JPEG files are allowed for ID uploads.");
      return;
    }

    setCameraError("");

    const reader = new FileReader();

    reader.onload = (e) => {
      if (side === "front") {
        setFrontImg(e.target.result);
      } else if (side === "back") {
        setBackImg(e.target.result);
      } else if (side === "face") {
        setFaceImg(e.target.result);
      }

      auditLog.record("ID_IMAGE_UPLOADED", {
        side,
        idType,
        fileName: file.name,
        fileType: file.type,
        size: file.size,
      });
    };

    reader.readAsDataURL(file);
  };

  // Replace your existing runVerification function with this

  const runVerification = async () => {
    stopCamera();
    setStep("processing");
    auditLog.record("OCR_STARTED", { idType });

    try {
      const verifyRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/verify-id`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            frontImage: frontImg,
            backImage: backImg,
            idType,
          }),
        },
      );

      if (!verifyRes.ok) throw new Error(`Server error ${verifyRes.status}`);

      const verifyResult = await verifyRes.json();

      if (!verifyResult.success) {
        setOcrResult({
          reason:
            verifyResult.error || "ID verification failed. Please try again.",
        });
        setIdValid(false);
        setStep("result");
        return;
      }

      const data = verifyResult.data || {};

      if (!data.isValid) {
        setOcrResult(data);
        setIdValid(false);
        setStep("result");
        auditLog.record("OCR_INVALID", { reason: data.reason });
        return;
      }

      const faceRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/face-match`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            faceImage: faceImg,
            idImage: frontImg,
          }),
        },
      );

      if (!faceRes.ok)
        throw new Error(`Face match server error ${faceRes.status}`);

      const faceResult = await faceRes.json();

      if (!faceResult.success || !faceResult.matched) {
        setOcrResult({
          ...data,
          isValid: false,
          reason:
            faceResult.reason ||
            "Face does not match the ID photo. Please retake your selfie.",
          faceScore: faceResult.score || 0,
        });
        setIdValid(false);
        setStep("result");
        auditLog.record("FACE_MATCH_FAILED", { score: faceResult.score });
        return;
      }

      // ── Step 3: All passed ────────────────────────────────────────────
      auditLog.record("FACE_MATCH_PASSED", { score: faceResult.score });

      const merged = {
        ...data,
        faceScore: faceResult.score,
      };

      setOcrResult(merged);
      setEditedOcr({
        lastName: (merged.lastName || "").toUpperCase(),
        firstName: (merged.firstName || "").toUpperCase(),
        middleName: (merged.middleName || "").toUpperCase(),
        suffix: (merged.suffix || "").toUpperCase(),
        dob: merged.dob || "",
        idNumber: (merged.idNumber || "").toUpperCase(),
        expiryDate: merged.expiryDate || "",
      });
      auditLog.record("OCR_COMPLETED", {
        confidence: merged.confidence,
        idType,
      });
      setIsEditing(true); // open edit mode so user can fix any missed fields
      setIdValid(true);
      setStep("result");
    } catch (err) {
      console.error("verify-id error:", err);
      auditLog.record("OCR_ERROR", { error: err.message });
      setOcrResult({
        reason: "Something went wrong during verification. Please try again.",
      });
      setIdValid(false);
      setStep("result");
    }
  };

  // ── Confirm & fill form ─────────────────────────────────────────
  const confirmAndFill = async () => {
    auditLog.record("ID_DATA_ACCEPTED", { idType, ocrResult });

    const formatDob = (raw) => {
      if (!raw) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const [dd, mm, yyyy] = raw.split("/");
        return `${yyyy}-${mm}-${dd}`;
      }
      return raw;
    };

    let frontImgUrl = frontImg;
    try {
      const blob = await (await fetch(frontImg)).blob();
      const formData = new FormData();
      formData.append("file", blob, "id-front.jpg");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload-id-image`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (res.ok) {
        const { url } = await res.json();
        frontImgUrl = url;
      }
    } catch (err) {
      console.error("ID image upload failed, falling back to base64:", err);
    }

    const merged = { ...ocrResult, ...editedOcr };
    const { address, ...mergedWithoutAddress } = merged;

    onComplete({
      ocrResult: { ...mergedWithoutAddress, dob: formatDob(merged.dob) },
      idType,
      idValid,
      frontImg: frontImgUrl,
      backImg,
      faceImg,
    });
    onClose();
  };

  if (!open) return null;

  // face step is now the last step before verify (no didit/processing in nav)
  const STEPS = ["type", "front", "back", "face", "processing", "result"];
  const STEP_LABELS = [
    "Select ID",
    "Front",
    "Back",
    "Face",
    "Processing",
    "Result",
  ];
  const stepIdx = STEPS.indexOf(step);

  return (
    <div style={S.overlay}>
      <div
        style={{
          ...S.modalBox,
          width: 560,
          maxWidth: "95vw",
          textAlign: "left",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: COLOR.green900,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Shield size={18} color={COLOR.white} />
          </div>
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: COLOR.ink,
              }}
            >
              ID Verification
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: COLOR.muted }}>
              Powered by ID Analyzer
            </p>
          </div>
          <button
            style={{ ...S.closeBtn, position: "static", marginLeft: "auto" }}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {STEP_LABELS.map((label, i) => (
            <div
              key={label}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 99,
                background: i <= stepIdx ? COLOR.green900 : COLOR.line,
              }}
            />
          ))}
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* ── Select ID type ── */}
          {step === "type" && (
            <div>
              <p style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 14 }}>
                Select your Government-Issued ID:
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                {VALID_ID_TYPES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setIdType(t)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 600,
                      border:
                        idType === t
                          ? `2px solid ${COLOR.green900}`
                          : `1.5px solid ${COLOR.line}`,
                      background: idType === t ? COLOR.tintGreen : COLOR.cream,
                      color: idType === t ? COLOR.green900 : COLOR.ink,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all .15s",
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <button
                disabled={!idType}
                onClick={() => setStep("front")}
                style={{
                  ...S.btn,
                  ...S.btnSolid,
                  width: "100%",
                  marginTop: 16,
                  opacity: idType ? 1 : 0.5,
                }}
              >
                Continue — Scan Front Side{" "}
                <ArrowRight size={14} style={{ marginLeft: 6 }} />
              </button>
            </div>
          )}

          {/* ── Front image ── */}
          {step === "front" && (
            <div>
              <p style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 14 }}>
                Scan FRONT side of your{" "}
                <span style={{ color: COLOR.green900 }}>{idType}</span>
              </p>
              <p
                style={{ margin: "0 0 12px", fontSize: 12, color: COLOR.muted }}
              >
                Ensure good lighting. All text must be clearly visible. Accepted
                format: JPG/JPEG only.
              </p>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <div
                  style={{
                    width: "100%",
                    height: 180,
                    overflow: "hidden",
                    borderRadius: 10,
                    background: "#f0f0f0",
                    border: `2px dashed ${COLOR.line}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {frontImg ? (
                    <img
                      src={frontImg}
                      alt="Front ID"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transform: `scale(${frontZoom}) rotate(${frontRotation}deg)`,
                        transition: "transform .2s",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Camera size={32} color={COLOR.muted} />
                      <span style={{ fontSize: 12, color: COLOR.muted }}>
                        No image uploaded yet
                      </span>
                    </div>
                  )}
                </div>
                {frontImg && (
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 6,
                    }}
                  >
                    <button
                      onClick={() => setFrontZoom((z) => Math.min(z + 0.25, 3))}
                      style={S.zoomBtn}
                    >
                      <ZoomIn size={14} />
                    </button>
                    <button
                      onClick={() =>
                        setFrontZoom((z) => Math.max(z - 0.25, 0.5))
                      }
                      style={S.zoomBtn}
                    >
                      <ZoomOut size={14} />
                    </button>
                    <button
                      onClick={() => setFrontRotation((r) => r - 90)}
                      style={S.zoomBtn}
                    >
                      <RotateCcw size={14} />
                    </button>
                  </div>
                )}
              </div>
              <input
                ref={frontRef}
                type="file"
                accept=".jpg,.jpeg,image/jpeg"
                style={{ display: "none" }}
                onChange={(e) => handleFile("front", e.target.files[0])}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => frontRef.current.click()}
                  style={{ ...S.btn, ...S.btnOutline, flex: 1 }}
                >
                  <Upload size={14} style={{ marginRight: 6 }} /> Upload Image
                </button>
                <button
                  disabled={!frontImg}
                  onClick={() => setStep("back")}
                  style={{
                    ...S.btn,
                    ...S.btnSolid,
                    flex: 1,
                    opacity: frontImg ? 1 : 0.5,
                  }}
                >
                  Next: Back Side{" "}
                  <ArrowRight size={14} style={{ marginLeft: 6 }} />
                </button>
              </div>
            </div>
          )}

          {/* ── Back image ── */}
          {step === "back" && (
            <div>
              <p style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 14 }}>
                Scan BACK side of your{" "}
                <span style={{ color: COLOR.green900 }}>{idType}</span>
              </p>
              <p
                style={{ margin: "0 0 12px", fontSize: 12, color: COLOR.muted }}
              >
                Upload the back of your ID. This helps verify authenticity.
              </p>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <div
                  style={{
                    width: "100%",
                    height: 180,
                    overflow: "hidden",
                    borderRadius: 10,
                    background: "#f0f0f0",
                    border: `2px dashed ${COLOR.line}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {backImg ? (
                    <img
                      src={backImg}
                      alt="Back ID"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transform: `scale(${backZoom}) rotate(${backRotation}deg)`,
                        transition: "transform .2s",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <Camera size={32} color={COLOR.muted} />
                      <span style={{ fontSize: 12, color: COLOR.muted }}>
                        No image uploaded yet
                      </span>
                    </div>
                  )}
                </div>
                {backImg && (
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 6,
                    }}
                  >
                    <button
                      onClick={() => setBackZoom((z) => Math.min(z + 0.25, 3))}
                      style={S.zoomBtn}
                    >
                      <ZoomIn size={14} />
                    </button>
                    <button
                      onClick={() =>
                        setBackZoom((z) => Math.max(z - 0.25, 0.5))
                      }
                      style={S.zoomBtn}
                    >
                      <ZoomOut size={14} />
                    </button>
                    <button
                      onClick={() => setBackRotation((r) => r - 90)}
                      style={S.zoomBtn}
                    >
                      <RotateCcw size={14} />
                    </button>
                  </div>
                )}
              </div>
              <input
                ref={backRef}
                type="file"
                accept=".jpg,.jpeg,image/jpeg"
                style={{ display: "none" }}
                onChange={(e) => handleFile("back", e.target.files[0])}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setStep("front")}
                  style={{ ...S.btn, ...S.btnOutline }}
                >
                  ← Back
                </button>
                <button
                  onClick={() => backRef.current.click()}
                  style={{ ...S.btn, ...S.btnOutline, flex: 1 }}
                >
                  <Upload size={14} style={{ marginRight: 6 }} /> Upload Image
                </button>
                <button
                  disabled={!backImg}
                  onClick={() => setStep("face")}
                  style={{
                    ...S.btn,
                    ...S.btnSolid,
                    flex: 1,
                    opacity: backImg ? 1 : 0.5,
                  }}
                >
                  Next: Face Scan{" "}
                  <ArrowRight size={14} style={{ marginLeft: 6 }} />
                </button>
              </div>
            </div>
          )}

          {/* ── Face scan ── */}
          {step === "face" && (
            <div>
              <p
                style={{
                  margin: "0 0 4px",
                  fontWeight: 700,
                  fontSize: 14,
                  color: COLOR.ink,
                }}
              >
                Face Verification
              </p>
              <p
                style={{
                  margin: "0 0 14px",
                  fontSize: 12,
                  color: COLOR.muted,
                  lineHeight: 1.6,
                }}
              >
                Position your face inside the oval and ensure good lighting.
              </p>

              {/* Camera viewport */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: 300,
                  borderRadius: 16,
                  overflow: "hidden",
                  background: faceImg ? "#000" : "#0d1117",
                  marginBottom: 14,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                }}
              >
                {/* Live video */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: "scaleX(-1)",
                    display: cameraActive && !faceImg ? "block" : "none",
                  }}
                />

                {/* Captured photo */}
                {faceImg && (
                  <img
                    src={faceImg}
                    alt="Face capture"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}

                {/* Idle state — camera not started */}
                {!cameraActive && !faceImg && !cameraError && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: "rgba(14,59,34,0.12)",
                        border: "2px dashed rgba(14,59,34,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Camera size={34} color="rgba(216,242,76,0.85)" />
                    </div>
                    <span
                      style={{
                        fontSize: 13,
                        color: "rgba(255,255,255,0.5)",
                        fontWeight: 500,
                      }}
                    >
                      Camera not started
                    </span>
                  </div>
                )}

                {/* Camera error */}
                {cameraError && !faceImg && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 24px",
                      textAlign: "center",
                      gap: 8,
                    }}
                  >
                    <AlertCircle size={28} color="#ef4444" />
                    <p
                      style={{
                        fontSize: 12,
                        color: "#ef4444",
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                      {cameraError}
                    </p>
                  </div>
                )}

                {/* Oval face guide overlay — only when camera active */}
                {cameraActive && !faceImg && (
                  <>
                    {/* Dark overlay with oval cutout via box-shadow trick */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 150,
                          height: 195,
                          borderRadius: "50%",
                          border: "3px solid rgba(216,242,76,0.95)",
                          boxShadow: "0 0 0 9999px rgba(0,0,0,0.52)",
                          position: "relative",
                        }}
                      >
                        {/* Corner tick marks */}
                        {[
                          {
                            top: -3,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 28,
                            height: 3,
                            borderRadius: 2,
                          },
                          {
                            bottom: -3,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 28,
                            height: 3,
                            borderRadius: 2,
                          },
                        ].map((s, i) => (
                          <div
                            key={i}
                            style={{
                              position: "absolute",
                              background: COLOR.lime,
                              ...s,
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Instruction label at bottom */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 14,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        zIndex: 3,
                        pointerEvents: "none",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#fff",
                          background: "rgba(0,0,0,0.55)",
                          borderRadius: 20,
                          padding: "4px 14px",
                          letterSpacing: 0.3,
                        }}
                      >
                        Center your face in the oval
                      </span>
                    </div>
                  </>
                )}

                {/* Countdown bubble */}
                {countdown !== null && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        width: 15,
                        height: 15,
                        borderRadius: "50%",
                        background: "rgba(14,59,34,0.88)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 25,
                        fontWeight: 500,
                        color: "#fff",
                        boxShadow: "0 0 0 8px rgba(216,242,76,0.3)",
                      }}
                    >
                      {countdown}
                    </div>
                  </div>
                )}

                {/* Success checkmark overlay on captured photo */}
                {faceImg && (
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      zIndex: 4,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: COLOR.green900,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    }}
                  >
                    <CheckCircle2 size={20} color="#fff" />
                  </div>
                )}
              </div>

              <canvas ref={canvasRef} style={{ display: "none" }} />

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 8 }}>
                {!faceImg && !cameraActive && (
                  <button
                    onClick={startCamera}
                    style={{
                      ...S.btn,
                      ...S.btnSolid,
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "13px 20px",
                      fontSize: 13,
                    }}
                  >
                    <Camera size={16} /> Open Camera
                  </button>
                )}

                {cameraActive && !faceImg && (
                  <>
                    <button
                      onClick={stopCamera}
                      style={{
                        ...S.btn,
                        ...S.btnOutline,
                        padding: "13px 16px",
                        fontSize: 13,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={startCountdown}
                      disabled={countdown !== null}
                      style={{
                        ...S.btn,
                        ...S.btnSolid,
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        padding: "13px 20px",
                        fontSize: 13,
                        opacity: countdown !== null ? 0.65 : 1,
                        transition: "opacity .2s",
                      }}
                    >
                      {countdown !== null ? (
                        `Taking in ${countdown}…`
                      ) : (
                        <>
                          <span style={{ fontSize: 16 }}></span> Take Photo
                        </>
                      )}
                    </button>
                  </>
                )}

                {faceImg && (
                  <button
                    onClick={() => {
                      setFaceImg(null);
                      startCamera();
                    }}
                    style={{
                      ...S.btn,
                      ...S.btnOutline,
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "13px 20px",
                      fontSize: 13,
                    }}
                  >
                    <RotateCcw size={15} /> Retake Photo
                  </button>
                )}
              </div>

              {/* Tips */}
              {!faceImg && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 14px",
                    background: COLOR.tintLime,
                    borderRadius: 10,
                    border: `1px solid ${COLOR.line}`,
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  <Info
                    size={14}
                    color={COLOR.green900}
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11,
                      color: COLOR.ink,
                      lineHeight: 1.6,
                    }}
                  >
                    <strong>Tips:</strong> Face forward, remove glasses if
                    possible, ensure your face is evenly lit with no harsh
                    shadows.
                  </p>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => {
                    stopCamera();
                    setStep("back");
                  }}
                  style={{
                    ...S.btn,
                    ...S.btnOutline,
                    padding: "13px 16px",
                    fontSize: 13,
                  }}
                >
                  ← Back
                </button>
                <button
                  disabled={!faceImg}
                  onClick={runVerification}
                  style={{
                    ...S.btn,
                    ...S.btnSolid,
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "13px 20px",
                    fontSize: 13,
                    opacity: faceImg ? 1 : 0.45,
                    transition: "opacity .2s",
                  }}
                >
                  <Shield size={15} /> Verify ID
                </button>
              </div>
            </div>
          )}

          {/* ── Processing ── */}
          {step === "processing" && (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  border: `5px solid ${COLOR.line}`,
                  borderTop: `5px solid ${COLOR.green900}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 20px",
                }}
              />
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: COLOR.green900,
                  margin: "0 0 8px",
                }}
              >
                Verifying ID…
              </p>
              <p style={{ color: COLOR.muted, fontSize: 13, margin: 0 }}>
                Analyzing your document. Please wait.
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ── Result ── */}
          {step === "result" && ocrResult && (
            <div>
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  marginBottom: 16,
                  background: idValid ? COLOR.tintGreen : "#fdecea",
                  border: `1.5px solid ${idValid ? COLOR.line : "#ef9a9a"}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {idValid ? (
                  <CheckCircle2 size={20} color={COLOR.green900} />
                ) : (
                  <AlertCircle size={20} color="#c62828" />
                )}
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      fontSize: 14,
                      color: idValid ? COLOR.green900 : "#b71c1c",
                    }}
                  >
                    {idValid
                      ? "ID Validated Successfully"
                      : "ID Validation Failed"}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: idValid ? COLOR.green700 : "#c62828",
                    }}
                  >
                    {idValid
                      ? `Confidence: ${(ocrResult.confidence * 100).toFixed(0)}%`
                      : "Please upload a valid, clear government ID."}
                  </p>
                </div>
              </div>

              {idValid && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      margin: "0 0 10px",
                    }}
                  >
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        margin: 0,
                        color: COLOR.ink,
                      }}
                    >
                      Extracted Information (will auto-fill form):
                    </p>
                    <button
                      onClick={() => {
                        if (isEditing)
                          setOcrResult((prev) => ({ ...prev, ...editedOcr }));
                        setIsEditing((e) => !e);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: isEditing ? COLOR.green900 : COLOR.muted,
                        padding: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <Pencil size={14} />
                      {isEditing ? "Done" : "Edit"}
                    </button>
                  </div>
                  <div
                    style={{
                      background: COLOR.cream,
                      border: `1.5px solid ${COLOR.line}`,
                      borderRadius: 10,
                      padding: "10px 14px",
                      marginBottom: 16,
                    }}
                  >
                    {[
                      ["Last Name", "lastName"],
                      ["First Name", "firstName"],
                      ["Middle Name", "middleName"],
                      ["Suffix", "suffix"],
                      ["Date of Birth", "dob"],
                      ["ID Number", "idNumber"],
                      ["Expiry Date", "expiryDate"],
                    ].map(([label, field]) => (
                      <div
                        key={label}
                        style={{
                          display: "flex",
                          gap: 12,
                          fontSize: 13,
                          padding: "5px 0",
                          borderBottom: `1px solid ${COLOR.line}`,
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            color: COLOR.muted,
                            width: 110,
                            flexShrink: 0,
                          }}
                        >
                          {label}
                        </span>
                        {isEditing ? (
                          <input
                            value={editedOcr[field] || ""}
                            onChange={(e) =>
                              setEditedOcr((p) => ({
                                ...p,
                                [field]: e.target.value.toUpperCase(),
                              }))
                            }
                            style={{
                              flex: 1,
                              border: `1.5px solid ${COLOR.line}`,
                              borderRadius: 8,
                              padding: "4px 8px",
                              fontSize: 13,
                              fontFamily: "inherit",
                              outline: "none",
                            }}
                          />
                        ) : (
                          <span style={{ fontWeight: 600, color: COLOR.ink }}>
                            {editedOcr[field] || ocrResult[field] || "—"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "10px 12px",
                      background: "#fff8e1",
                      borderRadius: 8,
                      border: "1px solid #ffe082",
                      marginBottom: 14,
                    }}
                  >
                    <Lock
                      size={14}
                      color="#e65100"
                      style={{ flexShrink: 0, marginTop: 1 }}
                    />
                    <p
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#c62828",
                        margin: 0,
                      }}
                    >
                      The name registered in this application is
                      NON-TRANSFERABLE and must match the approved franchisee's
                      legal identity.
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => setStep("type")}
                      style={{ ...S.btn, ...S.btnOutline }}
                    >
                      Rescan ID
                    </button>
                    <button
                      onClick={confirmAndFill}
                      style={{ ...S.btn, ...S.btnSolid, flex: 1 }}
                    >
                      Use This Data & Continue{" "}
                      <ArrowRight size={14} style={{ marginLeft: 6 }} />
                    </button>
                  </div>
                </>
              )}

              {!idValid && (
                <div>
                  {ocrResult?.reason && (
                    <p
                      style={{
                        fontSize: 13,
                        color: "#c62828",
                        fontWeight: 600,
                        marginBottom: 12,
                        padding: "10px 14px",
                        background: "#fdecea",
                        borderRadius: 8,
                      }}
                    >
                      Reason: {ocrResult.reason}
                    </p>
                  )}
                  <button
                    onClick={() => setStep("type")}
                    style={{ ...S.btn, ...S.btnSolid, width: "100%" }}
                  >
                    Try Again with a Valid ID
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CUSTOM SELECT ────────────────────────────────────────────────────────────
function CustomSelect({
  value,
  placeholder,
  options,
  onSelect,
  error,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          padding: "13px 14px",
          borderRadius: 12,
          border: error ? "1.5px solid #d32f2f" : `1.5px solid ${COLOR.line}`,
          fontSize: 14,
          fontFamily: "'Plus Jakarta Sans',sans-serif",
          color: value ? COLOR.ink : COLOR.muted,
          background: COLOR.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "border-color .2s",
          boxSizing: "border-box",
        }}
      >
        <span>{value || placeholder}</span>
        <ChevronDown
          size={16}
          color={COLOR.muted}
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
            flexShrink: 0,
          }}
        />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: `1.5px solid ${COLOR.line}`,
            borderRadius: 12,
            zIndex: 300,
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: `0 8px 32px ${COLOR.shadow}`,
          }}
        >
          {options.map((o) => (
            <div
              key={o || "none"}
              style={{
                padding: "11px 14px",
                cursor: "pointer",
                fontSize: 14,
                borderBottom: `1px solid ${COLOR.line}`,
                fontWeight: o === value ? 700 : 400,
                color: o === value ? COLOR.green900 : COLOR.ink,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = COLOR.tintLime)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
              onClick={() => {
                onSelect(o);
                setOpen(false);
              }}
            >
              {o || <span style={{ color: COLOR.muted }}>{placeholder}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FIELD ────────────────────────────────────────────────────────────────────
function Field({ label, required, error, children, half, style: extraStyle }) {
  return (
    <div
      style={{
        flex: half ? "0 0 calc(50% - 0.45rem)" : "1 1 100%",
        minWidth: half ? 140 : "auto",
        ...extraStyle,
      }}
    >
      {label && (
        <label
          style={{
            display: "block",
            fontSize: "0.82rem",
            fontWeight: 600,
            color: COLOR.ink,
            marginBottom: "0.4rem",
          }}
        >
          {label} {required && <span style={{ color: "#EF4444" }}>*</span>}
        </label>
      )}
      {children}
      {error && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: "#d32f2f",
            fontSize: "0.78rem",
            marginTop: "0.3rem",
            fontWeight: 600,
          }}
        >
          <AlertCircle size={12} style={{ marginRight: 4 }} />
          {error}
        </span>
      )}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: "0.2rem",
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: COLOR.green900,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={15} color={COLOR.lime} />
      </div>
      <div>
        <h3
          style={{
            fontSize: "0.95rem",
            fontWeight: 700,
            color: COLOR.green900,
            margin: 0,
          }}
        >
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: 11, color: COLOR.muted, margin: 0 }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function ApplyFranchise() {
  const navigate = useNavigate();
  const [alert, setAlert] = useState({
    open: false,
    type: "",
    message: "",
    onConfirm: null,
  });
  const showAlert = (type, message, onConfirm = null) =>
    setAlert({ open: true, type, message, onConfirm });
  const closeAlert = () =>
    setAlert((p) => ({ ...p, open: false, onConfirm: null }));

  // Modals
  const [showTerms, setShowTerms] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showIdScanner, setShowIdScanner] = useState(false);
  const [loading, setLoading] = useState(null);

  // Form state
  const [concept, setConcept] = useState("");
  const [civilStatus, setCivilStatus] = useState("");
  const [nationality, setNationality] = useState("");
  const [idVerified, setIdVerified] = useState(false);
  const [idData, setIdData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [letterOfIntent, setLetterOfIntent] = useState(null);
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(0);
  const loiRef = useRef();

  const [generatedOtp, setGeneratedOtp] = useState("");

  const [addrRegion, setAddrRegion] = useState("");
  const [addrProvince, setAddrProvince] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrBarangay, setAddrBarangay] = useState("");
  const [addrStreet, setAddrStreet] = useState("");

  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [barangays, setBarangays] = useState([]);

  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingBarangays, setLoadingBarangays] = useState(false);

  const PSGC = "https://psgc.gitlab.io/api";

  const today = new Date().toISOString().split("T")[0];
  const maxDob = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().split("T")[0];
  })();

  const [form, setForm] = useState({
    date: today,
    paymentMode: "",
    lastName: "",
    firstName: "",
    middleInitial: "",
    suffix: "",
    dob: "",
    dependents: "",
    gender: "",
    mobile: "",
    altMobile: "",
    email: "",
    spouseName: "",
    spouseOccupation: "",
    employmentType: "",
    yearsEmployer: "",
    income: "",
    employerName: "",
    businessAddress: "",
    position: "",
    businessNature: "",
    dateSigned: today,
    nationalityOther: "",
  });

  const textCap = [
    "lastName",
    "firstName",
    "spouseName",
    "spouseOccupation",
    "employerName",
    "position",
    "businessNature",
    "nationalityOther",
  ];

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "mobile" || name === "altMobile")
      value = value.replace(/\D/g, "").slice(0, 11);
    if (name === "middleInitial")
      value = value
        .replace(/[^a-zA-Z]/g, "")
        .slice(0, 1)
        .toUpperCase();
    if (textCap.includes(name)) value = capitalize(value);
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validateField = (name, value) => {
    const optional = [
      "spouseName",
      "spouseOccupation",
      "middleInitial",
      "suffix",
      "date",
      "dateSigned",
      "nationalityOther",
      "altMobile",
    ];
    if (optional.includes(name)) {
      if (name === "middleInitial" && value && !/^[A-Za-z]$/.test(value))
        return "1 letter only";
      if (name === "altMobile" && value && !/^09\d{9}$/.test(value))
        return "Please enter Philippine number (09**********)";
      return "";
    }
    if (!value) return "This field is required";
    if (name === "email" && !/\S+@\S+\.\S+/.test(value))
      return "Invalid email address";
    if (name === "mobile" && !/^09\d{9}$/.test(value))
      return "Please enter Philippine number (09**********)";
    if (name === "dob" && new Date(value) > new Date(maxDob))
      return "Must be at least 18 years old";
    if (name === "dependents" && (isNaN(value) || Number(value) < 0))
      return "Enter a valid number";
    if (name === "yearsEmployer" && (isNaN(value) || Number(value) < 0))
      return "Enter valid years";
    if (name === "income" && (isNaN(value) || Number(value) < 0))
      return "Enter valid income";
    return "";
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
  };

  const validate = () => {
    const e = {};
    Object.keys(form)
      .filter((k) => k !== "nationalityOther")
      .forEach((k) => {
        const err = validateField(k, form[k]);
        if (err) e[k] = err;
      });
    if (!concept) e.concept = "Please select a franchise concept";
    if (!civilStatus) e.civilStatus = "Please select civil status";
    if (!nationality) e.nationality = "Please select nationality";
    if (nationality === "Others" && !form.nationalityOther)
      e.nationalityOther = "Please specify your nationality";
    if (civilStatus === "Single") {
      delete e.spouseName;
      delete e.spouseOccupation;
    }
    if (!addrRegion) e.addrRegion = "Please select a region";
    if (provinces.length > 0 && !addrProvince)
      e.addrProvince = "Please select a province";
    if (!addrCity) e.addrCity = "Please select a city/municipality";
    if (!addrBarangay) e.addrBarangay = "Please select a barangay";
    if (!addrStreet) e.addrStreet = "Please enter a street address";
    if (!idVerified) e.idVerified = "Please complete ID verification";
    if (!letterOfIntent)
      e.letterOfIntent = "Please upload your Letter of Intent";
    if (!termsAccepted) e.terms = "You must accept the Terms and Conditions";
    if (!consentAccepted) e.consent = "You must give your data privacy consent";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const checkDuplicate = async (email, mobile) => {
    auditLog.record("DUPLICATE_CHECK", { email, mobile });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/check-duplicate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, mobile }),
        },
      );
      const data = await res.json();
      return data.exists;
    } catch (err) {
      console.error("Duplicate check error:", err);
      return false; // if check fails, allow submission
    }
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    setLoading("load");

    if (!validate()) {
      setLoading(null);
      showAlert(
        "error",
        "Please fill in all required fields and complete all verification steps before submitting.",
      );
      return;
    }

    const isDuplicate = await checkDuplicate(form.email, form.mobile);
    if (isDuplicate) {
      setLoading(null);
      auditLog.record("DUPLICATE_DETECTED", {
        email: form.email,
        mobile: form.mobile,
      });
      showAlert(
        "error",
        "An application with this email or mobile number already exists. Each person may only submit one application.",
      );
      return;
    }

    auditLog.record("FORM_VALIDATED", { email: form.email, concept });
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(otp);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/send-application-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: form.email.trim().toLowerCase(),
            otp,
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to send verification email.");
      }

      auditLog.record("OTP_EMAIL_SENT", {
        email: form.email,
        channel: "email",
      });

      setShowOtp(true);
    } catch (err) {
      console.error("Send OTP email error:", err);
      showAlert(
        "error",
        err.message ||
          "We could not send the verification code to your email. Please try again.",
      );
    } finally {
      setLoading(null);
    }
  };

  const handleIdComplete = ({
    ocrResult,
    idType,
    idValid,
    frontImg,
    backImg,
  }) => {
    if (!idValid) {
      showAlert(
        "error",
        ocrResult?.reason ||
          "ID validation failed. Please use a valid government-issued ID.",
      );
      return;
    }

    setIdVerified(true);
    setIdData({ ocrResult, idType, idValid, frontImg }); // ← add frontImg here

    auditLog.record("ID_AUTOFILL", { idType });

    setForm((p) => ({
      ...p,
      lastName: ocrResult.lastName
        ? capitalize(ocrResult.lastName)
        : p.lastName,
      firstName: ocrResult.firstName
        ? capitalize(ocrResult.firstName)
        : p.firstName,
      middleInitial: ocrResult.middleName
        ? ocrResult.middleName.charAt(0).toUpperCase()
        : p.middleInitial,
      suffix: ocrResult.suffix ? ocrResult.suffix.toUpperCase() : "",
      dob: ocrResult.dob || p.dob,
    }));

    if (ocrResult.address) setAddrStreet(ocrResult.address);

    showAlert(
      "success",
      "ID verified! Fields have been auto-filled. Please review and complete the remaining fields.",
    );
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleOtpVerified = async (success) => {
    setShowOtp(false);
    if (!success) {
      showAlert("error", "OTP verification failed. Please try again.");
      return;
    }
    auditLog.record("APPLICATION_SUBMIT_ATTEMPT", {
      email: form.email,
      concept,
    });

    const fullAddress = [
      addrStreet,
      getBarangayName(),
      getCityName(),
      getProvinceName(),
      getRegionName(),
    ]
      .filter(Boolean)
      .join(", ");
    const resolvedNationality =
      nationality === "Others" ? form.nationalityOther : nationality;
    const fullName = [
      form.firstName,
      form.middleInitial ? form.middleInitial + "." : "",
      form.lastName,
      form.suffix,
    ]
      .filter(Boolean)
      .join(" ");

    let letterOfIntentUrl = null;
    if (letterOfIntent) {
      letterOfIntentUrl = await uploadLoi(letterOfIntent);
    }

    const payload = {
      name: fullName,
      firstName: form.firstName,
      lastName: form.lastName,
      middleInitial: form.middleInitial || null,
      suffix: form.suffix || null,
      email: form.email,
      phone: form.mobile,
      altPhone: form.altMobile || null,
      franchise: concept,
      paymentMode: form.paymentMode,
      dob: form.dob,
      civilStatus,
      dependents: form.dependents,
      gender: form.gender,
      nationality: resolvedNationality,
      address: fullAddress,
      spouseName: form.spouseName || null,
      spouseOccupation: form.spouseOccupation || null,
      employmentType: form.employmentType,
      yearsEmployer: form.yearsEmployer,
      income: form.income,
      employerName: form.employerName,
      businessAddress: form.businessAddress,
      position: form.position,
      businessNature: form.businessNature,
      dateSigned: form.dateSigned,
      // ID verification fields
      idType: idData?.idType || null,
      idImage: idData?.frontImg || null,
      // Letter of intent
      letterOfIntent: letterOfIntentUrl,
      auditTrail: auditLog.getAll(),
    };
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        showAlert("error", `Server error: ${res.status}`);
        return;
      }
      const data = await res.json();
      if (data.success) {
        auditLog.record("APPLICATION_SUBMITTED", {
          success: true,
          applicationId: data.id,
        });
        showAlert(
          "success",
          "Application submitted successfully! Redirecting you to the home page...",
        );
        // Automatically redirect — no click required.
        setTimeout(() => {
          navigate("/");
        }, 1800);
      } else {
        showAlert("error", data.error || "Failed to submit. Please try again.");
      }
    } catch {
      showAlert(
        "error",
        "Failed to submit. Please check your connection and try again.",
      );
    }
  };

  useEffect(() => {
    const fetchRegions = async () => {
      setLoadingRegions(true);
      try {
        const res = await fetch(`${PSGC}/regions/`);
        const data = await res.json();
        setRegions(data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error("Failed to fetch regions:", err);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, []);

  useEffect(() => {
    if (!addrRegion) {
      setProvinces([]);
      setCities([]);
      setBarangays([]);
      return;
    }
    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      setAddrProvince("");
      setAddrCity("");
      setAddrBarangay("");
      setCities([]);
      setBarangays([]);
      try {
        const res = await fetch(`${PSGC}/regions/${addrRegion}/provinces/`);
        const data = await res.json();
        // Some regions (like NCR) have no provinces — fetch cities directly
        if (!Array.isArray(data) || data.length === 0) {
          const citRes = await fetch(
            `${PSGC}/regions/${addrRegion}/cities-municipalities/`,
          );
          const citData = await citRes.json();
          setCities(
            Array.isArray(citData)
              ? citData.sort((a, b) => a.name.localeCompare(b.name))
              : [],
          );
          setProvinces([]);
        } else {
          setProvinces(data.sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (err) {
        console.error("Failed to fetch provinces:", err);
      } finally {
        setLoadingProvinces(false);
      }
    };
    fetchProvinces();
  }, [addrRegion]);

  // Fetch cities when province changes
  useEffect(() => {
    if (!addrProvince) {
      setCities([]);
      setBarangays([]);
      return;
    }
    const fetchCities = async () => {
      setLoadingCities(true);
      setAddrCity("");
      setAddrBarangay("");
      setBarangays([]);
      try {
        const res = await fetch(
          `${PSGC}/provinces/${addrProvince}/cities-municipalities/`,
        );
        const data = await res.json();
        setCities(
          Array.isArray(data)
            ? data.sort((a, b) => a.name.localeCompare(b.name))
            : [],
        );
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [addrProvince]);

  // Fetch barangays when city changes
  useEffect(() => {
    if (!addrCity) {
      setBarangays([]);
      return;
    }
    const fetchBarangays = async () => {
      setLoadingBarangays(true);
      setAddrBarangay("");
      try {
        const res = await fetch(
          `${PSGC}/cities-municipalities/${addrCity}/barangays/`,
        );
        const data = await res.json();
        setBarangays(
          Array.isArray(data)
            ? data.sort((a, b) => a.name.localeCompare(b.name))
            : [],
        );
      } catch (err) {
        console.error("Failed to fetch barangays:", err);
      } finally {
        setLoadingBarangays(false);
      }
    };
    fetchBarangays();
  }, [addrCity]);

  // Helper to get name from code
  const getRegionName = () =>
    regions.find((r) => r.code === addrRegion)?.name || "";
  const getProvinceName = () =>
    provinces.find((p) => p.code === addrProvince)?.name || "";
  const getCityName = () => cities.find((c) => c.code === addrCity)?.name || "";
  const getBarangayName = () =>
    barangays.find((b) => b.code === addrBarangay)?.name || addrBarangay || "";

  useEffect(() => {
    const fields = [
      "paymentMode",
      "lastName",
      "firstName",
      "dob",
      "gender",
      "dependents",
      "mobile",
      "email",
      "employmentType",
      "yearsEmployer",
      "income",
      "employerName",
      "businessAddress",
      "position",
      "businessNature",
    ];
    let total = fields.length + 7;
    let filled = fields.filter((f) => form[f]).length;
    if (concept) filled++;
    if (civilStatus) filled++;
    if (nationality) filled++;
    if (addrRegion && addrCity && addrStreet) filled++;
    if (idVerified) filled++;
    if (letterOfIntent) filled++;
    if (termsAccepted && consentAccepted) filled++;
    if (civilStatus === "Married" || civilStatus === "Widowed") {
      total += 2;
      if (form.spouseName) filled++;
      if (form.spouseOccupation) filled++;
    }
    setProgress(Math.round((filled / total) * 100));
  }, [
    form,
    concept,
    civilStatus,
    nationality,
    addrRegion,
    addrCity,
    addrStreet,
    idVerified,
    letterOfIntent,
    termsAccepted,
    consentAccepted,
  ]);

  const steps = [
    { label: "Basic", pct: 20 },
    { label: "Personal", pct: 50 },
    { label: "Employment", pct: 80 },
    { label: "Done", pct: 100 },
  ];

  const inp = (name, placeholder, type = "text", extra = {}) => (
    <input
      style={{
        ...inpStyle,
        border: errors[name]
          ? "1.5px solid #d32f2f"
          : `1.5px solid ${COLOR.line}`,
      }}
      name={name}
      type={type}
      placeholder={placeholder}
      value={form[name]}
      onChange={handleChange}
      onBlur={handleBlur}
      {...extra}
    />
  );

  return (
    <div className="af-page">
      {loading === "load" && (
        <>
          <style>{`
            @keyframes spin {
              0%   { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(14,36,25,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: COLOR.white,
                borderRadius: 20,
                padding: "40px 48px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 16,
                boxShadow: `0 24px 80px ${COLOR.shadowStrong}`,
                minWidth: 260,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  border: `5px solid ${COLOR.line}`,
                  borderTop: `5px solid ${COLOR.green900}`,
                  borderRadius: "50%",
                  animation: "spin 0.9s linear infinite",
                }}
              />
              <p
                style={{
                  margin: 0,
                  fontWeight: 700,
                  fontSize: 16,
                  color: COLOR.green900,
                }}
              >
                Loading...
              </p>
            </div>
          </div>
        </>
      )}

      <AlertModal
        {...alert}
        onClose={closeAlert}
        onConfirm={alert.onConfirm ? () => alert.onConfirm() : null}
      />
      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
      <OtpModal
        open={showOtp}
        email={form.email}
        onVerify={handleOtpVerified}
        onClose={() => setShowOtp(false)}
        expectedOtp={generatedOtp}
        onResend={(newOtp) => setGeneratedOtp(newOtp)}
      />
      <IdScannerModal
        open={showIdScanner}
        onComplete={handleIdComplete}
        onClose={() => setShowIdScanner(false)}
      />

      {/* ── Nav ── */}
      <div className="af-nav">
        <div className="af-nav-inner">
          <Link
            to="/"
            className="af-back-link"
            onClick={() => window.scrollTo(0, 0)}
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
            <span>Back to Home</span>
          </Link>
          <div className="af-progress">
            <div className="af-steps">
              {steps.map((st) => (
                <span
                  key={st.label}
                  className={`af-step ${progress >= st.pct ? "active" : ""}`}
                >
                  {st.label}
                </span>
              ))}
            </div>
            <div className="af-bar-bg">
              <div className="af-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="af-progress-text">{progress}%</div>
          </div>
        </div>
      </div>

      <div className="af-content">
        <img src={logo} alt="iFranchise" className="af-logo" />

        <div className="af-card">
          <h2 className="af-title">Franchise Application</h2>
          <p className="af-sub">
            Complete the form below to begin your entrepreneurial journey
          </p>

          <form
            onSubmit={handleSubmitClick}
            style={{ display: "flex", flexDirection: "column", gap: "1.4rem" }}
          >
            {/* ── Basic Info ── */}
            <div className="af-section">
              <SectionHeader icon={FileText} title="Basic Information" />
              <div className="af-row">
                <Field label="Application Date" half>
                  <input style={disabledStyle} value={form.date} disabled />
                </Field>
                <Field
                  label="Payment Mode"
                  required
                  error={errors.paymentMode}
                  half
                >
                  <CustomSelect
                    value={form.paymentMode}
                    placeholder="Select payment mode"
                    options={PAYMENT_MODES}
                    error={errors.paymentMode}
                    onSelect={(v) => {
                      setForm((p) => ({ ...p, paymentMode: v }));
                      setErrors((p) => ({ ...p, paymentMode: "" }));
                    }}
                  />
                </Field>
              </div>
              <Field
                label="Chosen Franchise Concept"
                required
                error={errors.concept}
              >
                <CustomSelect
                  value={concept}
                  placeholder="Select a franchise concept"
                  options={CONCEPTS}
                  error={errors.concept}
                  onSelect={(v) => {
                    setConcept(v);
                    setErrors((p) => ({ ...p, concept: "" }));
                    if (v === "iPharma Mart") navigate("/apply-pharma");
                  }}
                />
              </Field>
            </div>

            {/* ── ID Verification ── */}
            <div className="af-section">
              <SectionHeader icon={Shield} title="ID Verification" />

              {!idVerified ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 16,
                      background: COLOR.tintGreen,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                    }}
                  >
                    <IdCard size={28} color={COLOR.green900} />
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: COLOR.ink,
                      marginBottom: 16,
                      fontWeight: 600,
                    }}
                  >
                    Please upload a valid ID
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowIdScanner(true)}
                    style={{ ...S.btn, ...S.btnSolid, padding: "12px 28px" }}
                  >
                    <Camera size={16} style={{ marginRight: 8 }} /> Upload ID
                  </button>
                  {errors.idVerified && (
                    <p
                      style={{
                        color: "#d32f2f",
                        fontSize: 12,
                        marginTop: 8,
                        fontWeight: 600,
                      }}
                    >
                      {errors.idVerified}
                    </p>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    background: COLOR.tintGreen,
                    borderRadius: 10,
                    border: `1.5px solid ${COLOR.line}`,
                  }}
                >
                  <CheckCircle2 size={24} color={COLOR.green900} />
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: 13,
                        color: COLOR.green900,
                      }}
                    >
                      ID Verified — {idData?.idType}
                    </p>
                    <p
                      style={{ margin: 0, fontSize: 11, color: COLOR.green700 }}
                    >
                      Please fill in your personal details
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIdVerified(false);
                      setIdData(null);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: COLOR.muted,
                      cursor: "pointer",
                      fontSize: 11,
                    }}
                  >
                    Rescan
                  </button>
                </div>
              )}
            </div>

            {/* ── Applicant Info ── */}
            {/* ── Applicant Info ── */}
            <div className="af-section">
              <SectionHeader
                icon={User}
                title="Applicant Information"
                subtitle={
                  idVerified
                    ? "Name extracted from your verified government ID"
                    : "Complete ID verification to automatically fill your name"
                }
              />

              {idVerified && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "9px 12px",
                    marginBottom: 12,
                    background: COLOR.tintGreen,
                    border: `1px solid ${COLOR.line}`,
                    borderRadius: 8,
                    fontSize: 11,
                    color: COLOR.green900,
                    fontWeight: 600,
                  }}
                >
                  <Lock size={13} />
                  Verified name fields are locked and cannot be manually edited.
                </div>
              )}

              <div className="af-row">
                <Field
                  label="Last Name"
                  required
                  error={errors.lastName}
                  style={{ flex: "2 1 150px" }}
                >
                  <input
                    name="lastName"
                    value={form.lastName}
                    disabled
                    placeholder="Dela Cruz"
                    onChange={handleChange}
                    style={{
                      ...inpStyle,
                      background: "#F1F3ED",
                      color: COLOR.muted,
                      cursor: "not-allowed",
                      border: `1.5px solid ${COLOR.line}`,
                    }}
                  />
                </Field>

                <Field
                  label="First Name"
                  required
                  error={errors.firstName}
                  style={{ flex: "2 1 150px" }}
                >
                  <input
                    name="firstName"
                    value={form.firstName}
                    disabled
                    placeholder="Juan"
                    onChange={handleChange}
                    style={{
                      ...inpStyle,
                      background: "#F1F3ED",
                      color: COLOR.muted,
                      cursor: "not-allowed",
                      border: `1.5px solid ${COLOR.line}`,
                    }}
                  />
                </Field>

                <Field
                  label="M.I."
                  error={errors.middleInitial}
                  style={{ flex: "0 0 72px" }}
                >
                  <input
                    name="middleInitial"
                    value={form.middleInitial}
                    disabled
                    placeholder="M"
                    maxLength={1}
                    onChange={handleChange}
                    style={{
                      ...inpStyle,
                      background: "#F1F3ED",
                      color: COLOR.muted,
                      cursor: "not-allowed",
                      border: `1.5px solid ${COLOR.line}`,
                    }}
                  />
                </Field>

                <Field label="Suffix" style={{ flex: "0 0 100px" }}>
                  <input
                    name="suffix"
                    value={form.suffix || ""}
                    disabled
                    placeholder="—"
                    style={{
                      ...inpStyle,
                      background: "#F1F3ED",
                      color: COLOR.muted,
                      cursor: "not-allowed",
                      border: `1.5px solid ${COLOR.line}`,
                    }}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Date of Birth" required error={errors.dob} half>
                  <input
                    style={{
                      ...inpStyle,
                      border: errors.dob
                        ? "1.5px solid #d32f2f"
                        : `1.5px solid ${COLOR.line}`,
                    }}
                    name="dob"
                    type="date"
                    min="1930-01-01"
                    max={maxDob}
                    value={form.dob}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>
                <Field
                  label="Civil Status"
                  required
                  error={errors.civilStatus}
                  half
                >
                  <CustomSelect
                    value={civilStatus}
                    placeholder="Select civil status"
                    options={CIVIL}
                    error={errors.civilStatus}
                    onSelect={(v) => {
                      setCivilStatus(v);
                      setErrors((p) => ({ ...p, civilStatus: "" }));
                    }}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Gender" required error={errors.gender} half>
                  <select
                    style={{
                      ...inpStyle,
                      border: errors.gender
                        ? "1.5px solid #d32f2f"
                        : `1.5px solid ${COLOR.line}`,
                    }}
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Prefer not to say</option>
                  </select>
                </Field>
                <Field
                  label="Nationality"
                  required
                  error={errors.nationality}
                  half
                >
                  <CustomSelect
                    value={nationality}
                    placeholder="Select nationality"
                    options={NATIONALITIES}
                    error={errors.nationality}
                    onSelect={(v) => {
                      setNationality(v);
                      setErrors((p) => ({
                        ...p,
                        nationality: "",
                        nationalityOther: "",
                      }));
                      if (v !== "Others")
                        setForm((p) => ({ ...p, nationalityOther: "" }));
                    }}
                  />
                </Field>
              </div>
              {nationality === "Others" && (
                <Field
                  label="Please specify nationality"
                  required
                  error={errors.nationalityOther}
                >
                  <input
                    style={{
                      ...inpStyle,
                      border: errors.nationalityOther
                        ? "1.5px solid #d32f2f"
                        : `1.5px solid ${COLOR.line}`,
                    }}
                    name="nationalityOther"
                    placeholder="e.g. American, Chinese, Japanese…"
                    value={form.nationalityOther}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>
              )}
              <div className="af-row">
                <Field
                  label="Number of Dependents"
                  required
                  error={errors.dependents}
                  half
                >
                  {inp("dependents", "0", "number", { min: 0 })}
                </Field>
                <Field
                  label="Mobile Number"
                  required
                  error={errors.mobile}
                  half
                >
                  {inp("mobile", "09123456789")}
                </Field>
              </div>
              <Field
                label="Alternate Mobile Number"
                error={errors.altMobile}
                half
              >
                {inp("altMobile", "0912 3456789 (optional)")}
              </Field>
              <Field label="Email Address" required error={errors.email}>
                {inp("email", "juandelacruz@email.com", "email")}
              </Field>
            </div>

            {/* ── Philippine Address ── */}
            <div className="af-section">
              <SectionHeader
                icon={MapPin}
                title="Present Address"
                subtitle="Powered by PSGC — Official Philippine address data"
              />

              {/* Region */}
              <Field label="Region" required error={errors.addrRegion}>
                <select
                  style={{
                    ...inpStyle,
                    border: errors.addrRegion
                      ? "1.5px solid #d32f2f"
                      : `1.5px solid ${COLOR.line}`,
                  }}
                  value={addrRegion}
                  onChange={(e) => {
                    setAddrRegion(e.target.value);
                    setErrors((p) => ({ ...p, addrRegion: "" }));
                  }}
                  disabled={loadingRegions}
                >
                  <option value="">
                    {loadingRegions ? "Loading regions..." : "Select Region"}
                  </option>
                  {regions.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Province — hidden for regions with no provinces (e.g. NCR) */}
              {provinces.length > 0 && (
                <Field label="Province" required error={errors.addrProvince}>
                  <select
                    style={{
                      ...inpStyle,
                      border: errors.addrProvince
                        ? "1.5px solid #d32f2f"
                        : `1.5px solid ${COLOR.line}`,
                    }}
                    value={addrProvince}
                    onChange={(e) => {
                      setAddrProvince(e.target.value);
                      setErrors((p) => ({ ...p, addrProvince: "" }));
                    }}
                    disabled={loadingProvinces || !addrRegion}
                  >
                    <option value="">
                      {loadingProvinces
                        ? "Loading provinces..."
                        : "Select Province"}
                    </option>
                    {provinces.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {/* City / Municipality */}
              <div className="af-row">
                <Field
                  label="City / Municipality"
                  required
                  error={errors.addrCity}
                  half
                >
                  <select
                    style={{
                      ...inpStyle,
                      border: errors.addrCity
                        ? "1.5px solid #d32f2f"
                        : `1.5px solid ${COLOR.line}`,
                    }}
                    value={addrCity}
                    onChange={(e) => {
                      setAddrCity(e.target.value);
                      setErrors((p) => ({ ...p, addrCity: "" }));
                    }}
                    disabled={loadingCities || !addrRegion}
                  >
                    <option value="">
                      {loadingCities
                        ? "Loading cities..."
                        : !addrRegion
                          ? "Select region first"
                          : "Select City/Municipality"}
                    </option>
                    {cities.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>

                {/* Barangay */}
                <Field
                  label="Barangay"
                  required
                  error={errors.addrBarangay}
                  half
                >
                  <select
                    style={{
                      ...inpStyle,
                      border: errors.addrBarangay
                        ? "1.5px solid #d32f2f"
                        : `1.5px solid ${COLOR.line}`,
                    }}
                    value={addrBarangay}
                    onChange={(e) => {
                      setAddrBarangay(e.target.value);
                      setErrors((p) => ({ ...p, addrBarangay: "" }));
                    }}
                    disabled={loadingBarangays || !addrCity}
                  >
                    <option value="">
                      {loadingBarangays
                        ? "Loading barangays..."
                        : !addrCity
                          ? "Select city first"
                          : "Select Barangay"}
                    </option>
                    {barangays.map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Street */}
              <Field
                label="House No. / Street / Subdivision"
                required
                error={errors.addrStreet}
              >
                <input
                  style={{
                    ...inpStyle,
                    border: errors.addrStreet
                      ? "1.5px solid #d32f2f"
                      : `1.5px solid ${COLOR.line}`,
                  }}
                  placeholder="House No., Street, Subdivision"
                  value={addrStreet}
                  onChange={(e) => {
                    setAddrStreet(e.target.value);
                    setErrors((p) => ({ ...p, addrStreet: "" }));
                  }}
                />
              </Field>

              {/* Address preview */}
              {addrRegion && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: "8px 12px",
                    background: COLOR.tintLime,
                    borderRadius: 8,
                    fontSize: 12,
                    color: COLOR.green900,
                  }}
                >
                  <MapPin size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    {[
                      addrStreet,
                      getBarangayName(),
                      getCityName(),
                      getProvinceName(),
                      getRegionName(),
                    ]
                      .filter(Boolean)
                      .join(", ") || "Address preview will appear here"}
                  </span>
                </div>
              )}
            </div>

            {/* ── Spouse ── */}
            {(civilStatus === "Married" || civilStatus === "Widowed") && (
              <div className="af-section">
                <SectionHeader icon={User} title="Spouse Information" />
                <Field label="Spouse Full Name" error={errors.spouseName}>
                  {inp("spouseName", "Spouse's Full Name")}
                </Field>
                <Field
                  label="Spouse Occupation"
                  error={errors.spouseOccupation}
                >
                  {inp("spouseOccupation", "Spouse's Current Occupation")}
                </Field>
              </div>
            )}

            {/* ── Employment ── */}
            <div className="af-section">
              <SectionHeader icon={Briefcase} title="Employment Information" />
              <Field
                label="Employment Type"
                required
                error={errors.employmentType}
              >
                <CustomSelect
                  value={form.employmentType}
                  placeholder="Select employment type"
                  options={EMPLOYMENT_TYPES}
                  error={errors.employmentType}
                  onSelect={(v) => {
                    setForm((p) => ({ ...p, employmentType: v }));
                    setErrors((p) => ({ ...p, employmentType: "" }));
                  }}
                />
              </Field>
              <div className="af-row">
                <Field
                  label="Years with Employer"
                  required
                  error={errors.yearsEmployer}
                  half
                >
                  {inp("yearsEmployer", "5", "number")}
                </Field>
                <Field
                  label="Monthly Income (₱)"
                  required
                  error={errors.income}
                  half
                >
                  {inp("income", "50000", "number")}
                </Field>
              </div>
              <Field
                label="Business/Company Name (No Acronyms)"
                required
                error={errors.employerName}
              >
                {inp(
                  "employerName",
                  "e.g. iFranchise Business and Services Corporation",
                )}
              </Field>
              <Field
                label="Business Address"
                required
                error={errors.businessAddress}
              >
                {inp("businessAddress", "Complete Business Address")}
              </Field>
              <div className="af-row">
                <Field
                  label="Position / Job Title"
                  required
                  error={errors.position}
                  half
                >
                  {inp("position", "e.g. Manager")}
                </Field>
                <Field
                  label="Nature of Business"
                  required
                  error={errors.businessNature}
                  half
                >
                  {inp("businessNature", "e.g. Retail, Manufacturing")}
                </Field>
              </div>
            </div>

            {/* ── Letter of Intent + ID Attachment ── */}
            <div className="af-section">
              <SectionHeader icon={FileText} title="Required Documents" />

              <Field
                label="Letter of Intent (PDF)"
                required
                error={errors.letterOfIntent}
              >
                <input
                  ref={loiRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    if (f.size > MAX_LOI_SIZE_BYTES) {
                      auditLog.record("LOI_REJECTED_SIZE", {
                        fileName: f.name,
                        size: f.size,
                      });
                      setErrors((p) => ({
                        ...p,
                        letterOfIntent: `File exceeds 100MB limit (${(f.size / (1024 * 1024)).toFixed(1)}MB). Please upload a smaller file.`,
                      }));
                      setLetterOfIntent(null);
                      e.target.value = "";
                      return;
                    }
                    auditLog.record("LOI_UPLOADED", {
                      fileName: f.name,
                      size: f.size,
                    });
                    setLetterOfIntent(f);
                    setErrors((p) => ({ ...p, letterOfIntent: "" }));
                  }}
                />
                <div
                  onClick={() => loiRef.current.click()}
                  style={{
                    border: errors.letterOfIntent
                      ? "2px dashed #d32f2f"
                      : `2px dashed ${COLOR.line}`,
                    borderRadius: 12,
                    padding: "20px 16px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: COLOR.cream,
                    transition: "all .2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = COLOR.tintLime)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = COLOR.cream)
                  }
                >
                  {letterOfIntent ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle2 size={20} color={COLOR.green900} />
                      <span
                        style={{
                          fontWeight: 700,
                          color: COLOR.green900,
                          fontSize: 13,
                        }}
                      >
                        {letterOfIntent.name}
                      </span>
                      <span style={{ color: COLOR.muted, fontSize: 11 }}>
                        ({(letterOfIntent.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                  ) : (
                    <>
                      <Upload
                        size={24}
                        color={COLOR.muted}
                        style={{ margin: "0 auto 8px" }}
                      />
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: 13,
                          color: COLOR.ink,
                        }}
                      >
                        Click to upload Letter of Intent
                      </p>
                      <p
                        style={{
                          margin: "4px 0 0",
                          fontSize: 11,
                          color: COLOR.muted,
                        }}
                      >
                        PDF only — max 100MB
                      </p>
                    </>
                  )}
                </div>
              </Field>

              {idVerified && (
                <div
                  style={{
                    padding: "12px 14px",
                    background: COLOR.tintLime,
                    borderRadius: 10,
                    border: `1.5px solid ${COLOR.line}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <CheckCircle2 size={16} color={COLOR.green900} />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: COLOR.green900,
                    }}
                  >
                    Valid ID attached — {idData?.idType} (from OCR scan)
                  </span>
                </div>
              )}
            </div>

            {/* ── Terms & Consent ── */}
            <div className="af-section">
              <SectionHeader icon={Shield} title="Terms & Consent" />

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setErrors((p) => ({ ...p, terms: "" }));
                  }}
                  style={{
                    marginTop: 2,
                    accentColor: COLOR.green900,
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontSize: 13, color: COLOR.ink, lineHeight: 1.6 }}
                >
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    style={{
                      background: "none",
                      border: "none",
                      color: COLOR.green900,
                      fontWeight: 700,
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 13,
                      textDecoration: "underline",
                      fontFamily: "inherit",
                    }}
                  >
                    Terms and Conditions
                  </button>{" "}
                  of iFranchise Business and Services Corporation.{" "}
                  <span style={{ color: "#EF4444" }}>*</span>
                </span>
              </label>
              {errors.terms && (
                <p
                  style={{
                    color: "#d32f2f",
                    fontSize: 12,
                    margin: "-4px 0 0",
                    fontWeight: 600,
                  }}
                >
                  {errors.terms}
                </p>
              )}

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={consentAccepted}
                  onChange={(e) => {
                    setConsentAccepted(e.target.checked);
                    setErrors((p) => ({ ...p, consent: "" }));
                  }}
                  style={{
                    marginTop: 2,
                    accentColor: COLOR.green900,
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontSize: 13, color: COLOR.ink, lineHeight: 1.6 }}
                >
                  I consent to the collection, processing, and use of my
                  personal data in accordance with the{" "}
                  <strong>
                    Data Privacy Act of 2012 (RA 10173) stated on no. 1 in Terms
                    and Conditions{" "}
                  </strong>{" "}
                  for the purpose of evaluating my franchise application.{" "}
                  <span style={{ color: "#EF4444" }}>*</span>
                </span>
              </label>
              {errors.consent && (
                <p
                  style={{
                    color: "#d32f2f",
                    fontSize: 12,
                    margin: "-4px 0 0",
                    fontWeight: 600,
                  }}
                >
                  {errors.consent}
                </p>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "10px 14px",
                  background: "#fff8e1",
                  borderRadius: 8,
                  border: "1px solid #ffe082",
                }}
              >
                <Lock
                  size={14}
                  color="#e65100"
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                <span style={{ fontSize: 12, color: "#e65100" }}>
                  <strong>Non-Transferability Notice:</strong> The franchise
                  registered under this application is strictly personal and
                  non-transferable. The name verified by your government-issued
                  ID will be the sole authorized franchisee.
                </span>
              </div>
            </div>

            <button type="submit" className="af-submit-btn">
              Submit Application
              <ArrowRight size={18} style={{ marginLeft: 8 }} />
            </button>
            <p className="af-footer-note">
              By submitting, you agree to our terms & consent.
            </p>
          </form>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        :root {
          --cream: #F6F7F1;
          --white: #FFFFFF;
          --ink: #12241B;
          --muted: #5C6B60;
          --green-900: #3b791e;
          --green-700: #438f1a;
          --green-600: #509820;
          --green-500: #3f811e;
          --lime: #bdd43c;
          --lime-ink: #24310C;
          --line: #E1E6D8;
          --shadow: rgba(50, 109, 32, 0.12);
          --shadow-strong: rgba(14, 59, 34, 0.24);
        }


        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .af-page {
          min-height: 100vh;
          font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
          background-image: linear-gradient(rgba(246,247,241,0.9), rgba(216,242,76,0.12)), url(${welcome});
          background-size: cover;
          background-attachment: fixed;
          padding-top: 90px;
          padding-bottom: 60px;
          color: var(--ink);
        }

        .af-nav {
          position: fixed; top: 0; left: 0; width: 100%;
          z-index: 100; padding: 14px 24px;
          background: rgba(246,247,241,0.92);
          backdrop-filter: blur(14px);
          box-shadow: 0 1px 0 var(--line);
        }
        .af-nav-inner {
          display: flex; align-items: center; gap: 20px; padding: 0 24px;
        }
        .af-back-link {
          display: flex; align-items: center; gap: 6px; color: var(--green-900);
          font-weight: 600; font-size: 0.9rem; text-decoration: none;
          white-space: nowrap; transition: opacity 0.2s;
        }
        .af-back-link:hover { opacity: 0.7; }
        .af-progress { flex: 1; display: flex; flex-direction: column; }
        .af-steps { display: flex; justify-content: space-between; margin-bottom: 6px; }
        .af-step { font-size: 0.7rem; font-weight: 600; color: var(--muted); transition: color 0.3s; }
        .af-step.active { color: var(--green-700); }
        .af-bar-bg { width: 100%; height: 8px; background: var(--line); border-radius: 999px; overflow: hidden; }
        .af-bar-fill { height: 100%; border-radius: 999px; background: var(--green-900); transition: width 0.4s ease; }
        .af-progress-text { margin-top: 4px; text-align: right; font-size: 0.75rem; font-weight: 600; color: var(--green-700); }

        .af-content { max-width: 860px; margin: 0 auto; padding: 0 1.5rem; display: flex; flex-direction: column; align-items: center; }
        .af-logo { height: 66px; object-fit: contain; margin-top: 12px; margin-bottom: 18px; }

        .af-card {
          background: rgba(255,255,255,0.97); width: 100%;
          border: 1px solid var(--line);
          border-radius: 24px; padding: 36px 30px 30px;
          box-shadow: 0 20px 50px var(--shadow);
        }
        .af-title { font-size: 23px; color: var(--green-900); font-weight: 800; letter-spacing: -0.01em; margin: 0 0 4px; text-align: center; }
        .af-sub { color: var(--muted); font-size: 13px; margin-bottom: 22px; text-align: center; }

        .af-section {
          background: var(--cream); border-radius: 18px; border: 1px solid var(--line);
          padding: 1.3rem 1.3rem 1.1rem; display: flex; flex-direction: column; gap: 0.9rem;
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .af-section:hover { box-shadow: 0 10px 26px var(--shadow); border-color: var(--green-600); }
        .af-row { display: flex; flex-wrap: wrap; gap: 0.9rem; }

        .af-card input[type="text"], .af-card input[type="email"], .af-card input[type="number"],
        .af-card input[type="date"], .af-card input[type="password"], .af-card textarea, .af-card select {
          width: 100%; padding: 13px 14px; border-radius: 12px; border: 1.5px solid var(--line);
          outline: none; font-size: 14px; font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--ink); background: var(--white); transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease; box-sizing: border-box;
        }
        .af-card input:hover, .af-card select:hover {
          border-color: var(--green-500) !important;
        }
        .af-card input:focus, .af-card textarea:focus, .af-card select:focus {
          border-color: var(--green-700) !important; background: #fff !important;
          box-shadow: 0 0 0 3px rgba(27,107,61,0.14);
        }
        .af-card input:disabled {
          background: var(--cream); color: var(--muted); cursor: not-allowed; border-color: var(--line) !important;
        }

        /* Smooth, interactive feel for every clickable control */
        button {
          transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, background 0.2s ease, filter 0.2s ease;
        }
        button:not(:disabled):hover {
          filter: brightness(1.04);
        }
        button:not(:disabled):active {
          transform: scale(0.97);
        }

        .af-submit-btn {
          display: flex; align-items: center; justify-content: center;
          width: 100%; padding: 15px; border-radius: 999px; border: none;
          background: var(--green-900); color: var(--white);
          font-weight: 700; cursor: pointer; font-size: 14px; letter-spacing: 0.3px;
          transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
          font-family: 'Plus Jakarta Sans', sans-serif;
          box-shadow: 0 10px 24px var(--shadow);
        }
        .af-submit-btn:hover { background: var(--green-700); box-shadow: 0 14px 30px var(--shadow-strong); transform: translateY(-1px); }
        .af-submit-btn:active { transform: translateY(0) scale(0.98); }
        .af-footer-note { text-align: center; font-size: 0.82rem; color: var(--muted); margin-top: -0.8rem; }
      `}</style>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const inpStyle = {
  width: "100%",
  padding: "13px 14px",
  borderRadius: 12,
  outline: "none",
  fontSize: 14,
  fontFamily: "'Plus Jakarta Sans',sans-serif",
  color: COLOR.ink,
  background: COLOR.white,
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  boxSizing: "border-box",
};

const disabledStyle = {
  ...inpStyle,
  background: COLOR.cream,
  color: COLOR.muted,
  cursor: "not-allowed",
  border: `1.5px solid ${COLOR.line}`,
};

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(14,36,25,0.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "af-fade-in 0.2s ease",
  },
  modalBox: {
    background: COLOR.white,
    borderRadius: 24,
    padding: "2.5rem 2rem 2rem",
    width: 380,
    maxWidth: "92vw",
    textAlign: "center",
    position: "relative",
    boxShadow: "0 30px 80px rgba(14,59,34,0.24)",
    animation: "af-pop-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1.2rem",
  },
  modalMsg: {
    fontSize: "1rem",
    color: COLOR.ink,
    lineHeight: 1.6,
    marginBottom: "1.5rem",
  },
  btnRow: { display: "flex", gap: 10, justifyContent: "center" },
  btn: {
    padding: "0.65rem 2rem",
    borderRadius: 999,
    fontSize: "0.95rem",
    fontWeight: 700,
    cursor: "pointer",
    border: "none",
    fontFamily: "'Plus Jakarta Sans',sans-serif",
  },
  btnSolid: {
    background: COLOR.green900,
    color: COLOR.white,
    boxShadow: "0 8px 20px rgba(14,59,34,0.22)",
  },
  btnOutline: {
    background: "transparent",
    color: COLOR.green900,
    border: `2px solid ${COLOR.green900}`,
  },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: COLOR.muted,
    padding: 4,
  },
  zoomBtn: {
    background: COLOR.white,
    border: `1px solid ${COLOR.line}`,
    borderRadius: 6,
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
};

if (
  typeof document !== "undefined" &&
  !document.getElementById("af-global-anim")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "af-global-anim";
  styleTag.textContent = `
    @keyframes af-fade-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes af-pop-in { from { opacity: 0; transform: scale(0.92) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
  `;
  document.head.appendChild(styleTag);
}
